import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Star, TrendingUp, Shield, Building2, ArrowRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { services } from '@/data/mock';

const iconMap = {
  MapPin: MapPin,
  Star: Star,
  TrendingUp: TrendingUp,
  Shield: Shield,
  Building2: Building2,
};

export const ServicesTeaser = () => {
  return (
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-ybro-primary font-semibold text-sm uppercase tracking-wider mb-4 block font-body">Our Core Services</span>
          <h2 className="text-3xl lg:text-4xl font-bold font-heading text-slate-900 mb-6">
            Everything you need to dominate local search
          </h2>
          <p className="text-lg text-slate-500 leading-relaxed font-body">
            From optimising your Google Business Profile to running performance‑driven local ad campaigns, yBro gives you an end‑to‑end local growth engine that is compliant, measurable and scalable.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            const serviceLink = service.title === 'Enterprise Reputation & Trust Governance'
              ? '/enterprise'
              : service.title === 'DMCC Act Compliance & Risk Audit'
                ? '/dmcc-compliance'
                : '/services';
            return (
              <Card 
                key={service.id}
                className="group relative overflow-hidden border-slate-200 hover:border-ybro-primary/40 hover:shadow-xl hover:shadow-ybro-primary/5 transition-all duration-500 bg-white"
              >
                {/* Hover gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-ybro-light/0 to-ybro-light/0 group-hover:from-ybro-light/50 group-hover:to-ybro-light/30 transition-all duration-500" />
                
                <CardHeader className="relative pb-4">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-ybro-light group-hover:bg-ybro-primary/20 flex items-center justify-center transition-colors duration-300">
                      {IconComponent && <IconComponent className="w-7 h-7 text-ybro-primary" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-medium font-body text-ybro-dark uppercase tracking-wider mb-2">{service.shortTitle}</p>
                      <CardTitle className="text-xl font-bold font-heading text-slate-900 leading-tight">
                        <Link to={serviceLink} className="hover:text-ybro-dark transition-colors">
                          {service.title}
                        </Link>
                      </CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="relative">
                  <CardDescription className="text-slate-500 text-base leading-relaxed font-body">
                    {service.description}{' '}
                    <Link
                      to={serviceLink}
                      className="text-ybro-dark font-semibold hover:text-ybro-primary transition-colors"
                    >
                      Learn more
                    </Link>
                  </CardDescription>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button 
            asChild
            variant="outline"
            size="lg"
            className="border-ybro-primary/40 text-ybro-dark hover:bg-ybro-light hover:border-ybro-primary transition-all duration-300 font-body"
          >
            <Link to="/services" className="flex items-center gap-2">
              View All Services
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};
